/**
 * Lieferscheine eines Projekts — was wann von wem kam.
 *
 * Benjamin, 02.10.2026: „die Liste für Lieferscheine kann keine Preise
 * enthalten — sowas steht auf Lieferscheinen nie drauf". Statt Buchung, Netto
 * und Brutto stehen hier die gelieferten Positionen und ob jede einem Material
 * zugeordnet ist (Migration 193, lieferschein_artikel.py). Was offen ist,
 * entscheidet man am Lieferanten unter „Neue Artikel prüfen" — der Hinweis
 * führt direkt dorthin. Ein Tipp auf „Geliefert" klappt die Positionen auf —
 * dort lassen sich Menge, Einheit und Material korrigieren
 * (`LieferscheinPositionen.tsx`).
 */
import { Fragment, useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import type { Dokument } from "../DokumentLightbox";
import type { LieferPositionKurz, ProjektBeleg } from "../../lib/types";
import LieferscheinPositionen from "./LieferscheinPositionen";

/** Ab so vielen Positionen wird gekürzt — die Liste bleibt eine Zeile je Lieferschein. */
const SICHTBAR = 2;

const menge = (p: LieferPositionKurz) =>
  [p.menge ? p.menge.toLocaleString("de-DE", { maximumFractionDigits: 2 }) : "", p.einheit].filter(Boolean).join(" ");

function Geliefert({ positionen }: { positionen: LieferPositionKurz[] }): ReactElement {
  if (positionen.length === 0) return <span className="is-muted">keine Positionen gelesen</span>;
  const rest = positionen.length - SICHTBAR;
  const sichtbar = rest === 1 ? positionen : positionen.slice(0, SICHTBAR);
  return (
    <span className="vg-ls-geliefert" title={positionen.map((p) => `${menge(p)} ${p.name}`.trim()).join("\n")}>
      {sichtbar.map((p, i) => (
        <span key={i} className="vg-ls-pos">
          {menge(p) && <span className="vg-ls-menge">{menge(p)}</span>} {p.name}
        </span>
      ))}
      {rest > 1 && <span className="is-muted">+ {rest} weitere</span>}
    </span>
  );
}

function Zuordnung({ beleg }: { beleg: ProjektBeleg }): ReactElement {
  const offen = beleg.lieferpositionen.filter((p) => p.offen).length;
  if (beleg.lieferpositionen.length === 0) return <span className="is-muted">—</span>;
  if (offen === 0) return <span className="is-muted">zugeordnet</span>;
  const text = `${offen} offen`;
  return beleg.lieferant_firma_id ? (
    <Link
      className="vg-proj-tag is-prio-hoch"
      to={`/lieferanten?lieferant=${encodeURIComponent(beleg.lieferant_firma_id)}`}
      title="Noch keinem Material zugeordnet — beim Lieferanten unter „Neue Artikel prüfen“ entscheiden"
    >
      {text}
    </Link>
  ) : (
    <span className="vg-proj-tag is-prio-hoch" title="Lieferant fehlt am Lieferschein — erst im Beleg setzen">{text}</span>
  );
}

export default function LieferscheinListe({ lieferscheine, projektId, onZeigen }: {
  lieferscheine: ProjektBeleg[];
  projektId: string;
  onZeigen: (d: Dokument) => void;
}): ReactElement {
  const [offen, setOffen] = useState<string | null>(null);
  return (
    <table className="vg-tabelle k-cardtable vg-ls-tabelle">
      <thead>
        <tr>
          <th>Datum</th><th>Lieferant</th><th>Geliefert</th><th>Zuordnung</th>
          <th className="is-aktion" />
        </tr>
      </thead>
      <tbody>
        {lieferscheine.map((b) => (
          <Fragment key={b.id}>
          <tr className={offen === b.id ? "ist-aufgeklappt" : undefined}>
            <td data-label="Datum">{b.datum ? new Date(b.datum).toLocaleDateString("de-DE") : "—"}</td>
            <td data-label="Lieferant" className="is-muted">{b.lieferant || "—"}</td>
            <td data-label="Geliefert">
              <button type="button" className="vg-ls-auf" aria-expanded={offen === b.id}
                title="Positionen ansehen und korrigieren" onClick={() => setOffen(offen === b.id ? null : b.id)}>
                <Geliefert positionen={b.lieferpositionen} />
                <span className="vg-ls-pfeil" aria-hidden="true">{offen === b.id ? "▾" : "▸"}</span>
              </button>
            </td>
            <td data-label="Zuordnung"><Zuordnung beleg={b} /></td>
            <td className="is-aktion" data-label="">
              <button
                type="button"
                className="vg-icon-btn"
                title="Lieferschein anzeigen"
                aria-label="Lieferschein anzeigen"
                onClick={() => onZeigen({
                  titel: b.lieferant || "Lieferschein",
                  unterzeile: `${b.datum || ""} · Lieferschein`,
                  url: `/api/kontor/belege/${b.id}/pdf`,
                  belegId: b.id,
                })}
              >
                ↗
              </button>
            </td>
          </tr>
          {offen === b.id && (
            <tr className="vg-ls-detail">
              <td colSpan={5} data-label="">
                <LieferscheinPositionen belegId={b.id} projektId={projektId} />
              </td>
            </tr>
          )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
